/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/demoCaseROIOverride.js
 *
 * DEMO ONLY — Hard-coded ROI trackingType override for one specific patient.
 * Applied AFTER getClassifiedROIContours, ONLY for non-RECIP guidelines.
 *
 * Background: the demo case PSMA_561766bb23d62cf8 has 21 GTV lesions + 2
 * reference ROIs (descending thoracic aorta, liver reference). For consistent
 * demo presentation across guidelines, ROI types are hard-coded here rather
 * than relying on the auto-classifier.
 *
 * DELETE THIS FILE once the demo material is moved off this patient OR
 * once the platform supports per-study ROI type templates.
 *
 * NOT used for RECIP 1.0 — RECIP has its own 5-category type system handled
 * by getRecipClassifiedROIContours.
 */

import { TRACKING_TYPES } from './guideline';

export const DEMO_PATIENT_ID = 'PSMA_561766bb23d62cf8';

/**
 * Name patterns for reference ROIs that should be excluded (not-included)
 * from RECIST-style classification. Case-insensitive substring match.
 */
const REFERENCE_ROI_PATTERNS = [
  'descending thoracic aorta',
  'ascending aorta',
  'aorta',
  'liver reference',
  'right liver lobe',
  'right hepatic lobe',
];

function isReferenceROIName(roiName) {
  if (!roiName) return false;
  const lower = String(roiName).toLowerCase();
  return REFERENCE_ROI_PATTERNS.some(p => lower.includes(p));
}

/**
 * Extract the GTV <N> numeric suffix. Returns null for non-GTV names.
 *   "GTV 0"  → 0,  "GTV 15" → 15,  "Aorta" → null
 */
function parseGtvIndex(roiName) {
  if (!roiName) return null;
  const m = String(roiName).match(/^GTV [_-]?(\d+)/i);
  return m ? Number(m[1]) : null;
}

/**
 * Map one ROI to a trackingType for the active guideline.
 * Method: ROIName "GTV<N>" regex (guards against re-import with renumbered ROIs).
 * Returns null if the ROI doesn't match either strategy.
 */
function mapDemoROI(roi, guidelineId) {
  // ROIName-based for resilience against ROINumber shifts
  const gtvIdx = parseGtvIndex(roi.ROIName);
  if (gtvIdx == null) return null;
  if (gtvIdx === 0) return TRACKING_TYPES.TARGET;
  if (gtvIdx >= 1 && gtvIdx <= 7) return TRACKING_TYPES.NONTARGET;
  if (gtvIdx === 8) return TRACKING_TYPES.NOTINCLUDED;
  if (gtvIdx >= 9 && gtvIdx <= 14) return TRACKING_TYPES.NONTARGET;
  if (gtvIdx >= 15 && gtvIdx <= 20) {
    return guidelineId === 'iRECIST'
      ? TRACKING_TYPES.NEW_NONTARGET
      : TRACKING_TYPES.NEW;
  }
  return null;
}

const PATIENT_OVERRIDE_HANDLERS = {
  [DEMO_PATIENT_ID]: (rois, criteria) => {
    if (criteria?.guidelineId === 'RECIP10') {
      return rois.map(roi => {
        if (roi.ROIName === 'GTV 8') {
          return {
            ...roi,
            trackingType: TRACKING_TYPES.LESION,
          };
        }
        return roi;
      });
    }

    return rois.map(roi => {
      // Reference ROIs (aorta, liver) — ROIName substring check runs first so
      // ROINumber ranges can't accidentally reclassify a reference ROI.
      if (isReferenceROIName(roi.ROIName)) {
        return {
          ...roi,
          trackingType: TRACKING_TYPES.NOTINCLUDED,
          measurable: false,
        };
      }
      const mappedType = mapDemoROI(roi, criteria?.guidelineId);
      if (mappedType == null) return roi;
      return { ...roi, trackingType: mappedType };
    });
  },
  ['2627919915']: (rois, criteria) => {
    const SKIP_CRITERIA = ['RECIP10', 'CUSTOM'];
    if (SKIP_CRITERIA.includes(criteria.guidelineId)) return rois;
    const TARGET_ROI_NAMES = ['ROI_8', 'ROI_34', 'ROI_56', 'ROI_42', 'ROI_2'];
    const number = Math.min(
      criteria.maxNumberOfTargets,
      criteria.maxNumberOfTargetsPerOrgan
    );
    const names = TARGET_ROI_NAMES.slice(0, number);
    return rois.map(roi => {
      if (names.includes(roi.ROIName)) {
        return { ...roi, trackingType: TRACKING_TYPES.TARGET };
      } else if (roi.trackingType === TRACKING_TYPES.TARGET) {
        return { ...roi, trackingType: TRACKING_TYPES.NONTARGET };
      }
      return roi;
    });
  },
};

/**
 * Apply the demo override. Returns a NEW array (does not mutate input).
 * Idempotent. No-op for non-demo patients and for RECIP 1.0.
 *
 * @param {Array}  rois
 * @param {object} criteria   must have guidelineId
 * @param {string} patientId
 * @returns {Array}
 */
export function applyDemoCaseROIOverride(rois, criteria, patientId) {
  const handler = PATIENT_OVERRIDE_HANDLERS[patientId];
  return handler ? handler(rois, criteria) : rois;
}
